import { StaticPageShell } from "./StaticPageShell";

export default function Privacy() {
  return (
    <StaticPageShell
      eyebrow="04 · Privacy"
      title="Privacy policy"
      lead="The short version: your data is yours. We collect the bare minimum to make Sabzy work and never sell it. Read on for the full breakdown."
      seo={{
        title: "Sabzy AI Privacy Policy — How we handle your data",
        description: "Sabzy AI privacy policy: what we collect, who we share with (OpenAI, Anthropic, Google, Tavily, AgentMail), retention, your rights, and how to delete your data.",
        path: "/privacy",
      }}
    >
      <section>
        <p className="text-sm text-muted-foreground"><b>Effective date:</b> February 2026</p>
      </section>

      <Section title="1. Who we are">
        <p>
          "Sabzy AI" (we, us, our) operates the websites sabzy.ai and sabzy.in and the chat application accessible there. Reach us at <a href="mailto:support@sabzy.in">support@sabzy.in</a>.
        </p>
      </Section>

      <Section title="2. What we collect">
        <ul className="list-disc pl-5 space-y-2">
          <li><b>Email address</b> — required for passwordless sign-in.</li>
          <li><b>One-time codes (OTPs)</b> — temporary, expire in 10 minutes.</li>
          <li><b>IP + coarse geo</b> (country / city) — for fraud prevention and basic analytics.</li>
          <li><b>Your messages and uploads</b> — to send to the model and show you the reply.</li>
          <li><b>Conversation history + memory profile</b> — stored on our servers so you have continuity across sessions. You can wipe it any time.</li>
          <li><b>Promo redemption history</b> — to enforce one-time / capped use.</li>
        </ul>
      </Section>

      <Section title="3. What we don't collect">
        <ul className="list-disc pl-5 space-y-2">
          <li>We don't collect passwords (sign-in is passwordless).</li>
          <li>We don't collect cards. (No paid tier at launch.)</li>
          <li>We don't use third-party advertising trackers, fingerprinting, or session replay.</li>
        </ul>
      </Section>

      <Section title="4. How we use the data">
        <ul className="list-disc pl-5 space-y-2">
          <li>To run the service — authenticate you, send your prompt to the model, return the reply.</li>
          <li>To make Sabzy feel like it remembers you (memory profile, cross-session context).</li>
          <li>To prevent abuse (rate limiting, anomaly detection on IP / geo).</li>
          <li>To answer your support emails.</li>
        </ul>
      </Section>

      <Section title="5. Who we share data with">
        <ul className="list-disc pl-5 space-y-2">
          <li><b>OpenAI, Anthropic and Google</b> — your prompt is sent to the model you selected (GPT, Claude or Gemini) so it can reply. Each provider processes inputs as a data processor under their own terms.</li>
          <li><b>Tavily</b> — when you trigger web search, your query (only the query) is sent to Tavily.</li>
          <li><b>AgentMail</b> — to deliver your sign-in code to your inbox.</li>
          <li><b>MongoDB Atlas (or equivalent)</b> — encrypted storage of your account, messages and memory.</li>
        </ul>
        <p>We don't sell your data. We don't train public models on your data.</p>
      </Section>

      <Section title="6. Retention">
        <p>
          We keep your account, chats and memory profile until you ask us to delete them. Email us from the address you signed up with and we'll purge within 24 hours. OTPs and contact-form messages are kept for 30 days then deleted automatically.
        </p>
      </Section>

      <Section title="7. Your rights">
        <p>
          You can request access, correction, export or deletion of your data at any time. Email <a href="mailto:support@sabzy.in">support@sabzy.in</a>. Users in the EU/UK can also lodge a complaint with their national data protection authority.
        </p>
      </Section>

      <Section title="8. Security">
        <p>
          Passwords aren't used. Sign-in is gated by short-lived OTPs. Data in transit is HTTPS / TLS only. Tokens are signed (JWT HS256) and expire in 30 days. We patch dependencies regularly. No system is perfect — please report security issues to <a href="mailto:support@sabzy.in">support@sabzy.in</a>.
        </p>
      </Section>

      <Section title="9. Changes to this policy">
        <p>
          We may update this policy. Material changes will be flagged in-app and via email to active users.
        </p>
      </Section>
    </StaticPageShell>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="text-xl font-semibold tracking-tight text-foreground">{title}</h2>
      <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}
